import { deepActiveElement, focusWithin } from "#ce/utils/focus.js";
import { isPresent } from "#ce/utils/props";

/**
 * Focus handling for sp-dialog. A native modal `<dialog>` traps Tab and inerts
 * the background, but it cannot move focus into content built from custom
 * elements (the inner controls live in nested shadow roots it does not reach)
 * nor restore focus to a non-focusable custom-element host on close. This covers
 * both: remember the opener, move focus inside, and restore it afterwards.
 *
 * This is a pure mechanism with no notion of a dialog session. The dialog
 * lifecycle decides when to capture (once, on open) and restore (on close).
 *
 * @param {import("vue").Ref<HTMLElement | null>} content - The dialog content ref.
 * @returns {{
 *   capture: () => void,
 *   moveIn: () => void,
 *   restore: (skip?: boolean) => void,
 *   release: () => void,
 * }}
 */
export function useDialogFocus(content) {
  let origin = null;

  // Remember the element to restore focus to when the dialog closes.
  function capture() {
    origin = deepActiveElement();
  }

  function focusIsInside() {
    return focusWithin(content.value);
  }

  /**
   * Returns the first element under the content carrying `focus-on-open`,
   * walking `<slot>` assignments and shadow roots. A marked shadow host resolves
   * to the marked element inside it (sp-text-field forwards the attribute to its
   * `<input>`). Returns null when nothing declares it. `focus-on-open="false"`
   * does not declare it: a boolean `false` bound to an element that is not yet
   * upgraded is written as that attribute string.
   *
   * The marker is not the native `autofocus`: the browser processes that on
   * insertion, while the dialog is still closed.
   *
   * @param {Node | null} node
   * @returns {Element | null}
   */
  function autofocusTarget(node = content.value) {
    if (!node) {
      return null;
    }
    const children = node instanceof HTMLSlotElement ? node.assignedElements({ flatten: true }) : node.children;
    for (const child of children) {
      if (isPresent(child.getAttribute("focus-on-open"))) {
        return autofocusTarget(child.shadowRoot) ?? child;
      }
      const found = autofocusTarget(child.shadowRoot) ?? autofocusTarget(child);
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * Moves focus to the `focus-on-open` target, or the content when there is none.
   * Does nothing while focus is already inside, so a field the user focused and
   * started typing into keeps it.
   */
  function moveIn() {
    if (focusIsInside()) {
      return;
    }
    (autofocusTarget() ?? content.value)?.focus({ preventScroll: true });
  }

  function release() {
    if (focusIsInside()) {
      deepActiveElement()?.blur?.();
    }
  }

  // Focus the user or a script moved elsewhere during the leave animation stays there.
  function focusIsUnclaimed() {
    const active = deepActiveElement();
    return !active || active === document.body || active === origin || focusIsInside();
  }

  function restore(skip = false) {
    if (!skip && focusIsUnclaimed()) {
      origin?.focus?.({ preventScroll: true });
    }
    origin = null;
  }

  return { capture, moveIn, restore, release };
}
